import type { AYamlConfig, BlameHunk, CommentBlocks, FileContent, GitRef, HighlightedLines, RepoDetails, TreeEntry, WoodpeckerConfig } from 'codesearch-backend'
import { createQuery } from '@tanstack/solid-query'
import { trpc } from '../trpc.ts'
import { navigate, route } from './router.ts'

export { route }

export function openSearch(query: string) {
  navigate({ kind: 'SEARCH', query })
}

export function openFile(repo: string, path: string, line?: number, gitRef = currentRef()) {
  navigate({ kind: 'VIEW', repo, path, line, gitRef })
}

export function openBrowse(repo: string, path = '', gitRef = currentRef()) {
  navigate({ kind: 'BROWSE', repo, path, gitRef })
}

/** the branch or tag being browsed; `undefined` is the indexed one (HEAD) */
export function currentRef(): string | undefined {
  const current = route()
  return current.kind === 'BROWSE' || current.kind === 'VIEW' ? current.gitRef : undefined
}

/** switch refs without losing your place in the tree */
export function openRef(gitRef: string | undefined) {
  const current = route()
  if (current.kind === 'VIEW') navigate({ ...current, gitRef, line: undefined })
  else if (current.kind === 'BROWSE') navigate({ ...current, gitRef })
}

export function goHome() {
  navigate({ kind: 'HOME' })
}

/** the repo/path currently being looked at, whether browsing or viewing a file */
export function currentLocation(): { repo: string, path: string } | null {
  const current = route()
  if (current.kind === 'BROWSE' || current.kind === 'VIEW') {
    return { repo: current.repo, path: current.path }
  }
  return null
}

/** a file as the ui sees it: highlighted, and pre-rendered when it's markdown */
export type ViewFileContent = FileContent & {
  tokens: HighlightedLines | null
  markdown: string | null
  comments: CommentBlocks
  ayaml: AYamlConfig | null
  woodpecker: WoodpeckerConfig | null
}

export function useFileContent() {
  return createQuery(() => {
    const current = route()
    const target = current.kind === 'VIEW' ? current : undefined
    return {
      queryKey: ['file', target?.repo, target?.path, target?.gitRef],
      queryFn: () => trpc.search.fileByPath.query({
        repo: target!.repo,
        path: target!.path,
        gitRef: target!.gitRef,
      }),
      enabled: !!target,
      // hold the file that's already on screen until the next one arrives; without
      // this every click swaps the whole pane out for a spinner and the view blinks
      placeholderData: (previous?: ViewFileContent) => previous,
    }
  })
}

export function useTree(repo: () => string | undefined, path: () => string) {
  return createQuery(() => ({
    queryKey: ['tree', repo(), path(), currentRef()],
    queryFn: () => trpc.search.tree.query({ repo: repo()!, path: path(), gitRef: currentRef() }),
    enabled: !!repo(),
    // keep the previous listing on screen while the next one loads, otherwise every
    // folder click blanks the panel and the page visibly flashes
    placeholderData: (previous?: TreeEntry[]) => previous,
  }))
}

/*
  the directory's readme, rendered. code search opens a folder on its readme rather
  than on the bare listing, and so do we - it's the difference between landing on a
  repo and landing in it
*/
export function useReadme(repo: () => string | undefined, path: () => string) {
  return createQuery(() => ({
    queryKey: ['readme', repo(), path(), currentRef()],
    queryFn: () => trpc.search.readme.query({ repo: repo()!, path: path(), gitRef: currentRef() }),
    enabled: !!repo(),
    staleTime: 60_000,
  }))
}

export function useRefs(repo: () => string | undefined) {
  return createQuery(() => ({
    queryKey: ['refs', repo()],
    queryFn: () => trpc.search.refs.query({ repo: repo()! }),
    enabled: !!repo(),
    // refs move about as often as somebody pushes, and this is only ever read to
    // fill a dropdown
    staleTime: 60_000,
    placeholderData: (previous?: GitRef[]) => previous,
  }))
}

/** blame walks history on the server, so it's only fetched once you ask for it */
export function useBlame(enabled: () => boolean) {
  return createQuery(() => {
    const current = route()
    const target = current.kind === 'VIEW' ? current : undefined
    return {
      queryKey: ['blame', target?.repo, target?.path, target?.gitRef],
      queryFn: () => trpc.search.blame.query({
        repo: target!.repo,
        path: target!.path,
        gitRef: target!.gitRef,
      }),
      enabled: !!target && enabled(),
      staleTime: 5 * 60_000,
      placeholderData: (previous?: BlameHunk[]) => previous,
    }
  })
}

/** whether the index is git-backed, and where its origin lives */
export function useRepoDetails(repo: () => string | undefined) {
  return createQuery(() => ({
    queryKey: ['repoDetails', repo()],
    queryFn: () => trpc.search.repoDetails.query({ repo: repo()! }),
    enabled: !!repo(),
    staleTime: 5 * 60_000,
    placeholderData: (previous?: RepoDetails | null) => previous,
  }))
}

/** one commit's details, for the blame popover. only fetched when one is opened */
export function useCommit(repo: () => string, id: () => string | undefined) {
  return createQuery(() => ({
    queryKey: ['commit', repo(), id()],
    queryFn: () => trpc.search.commit.query({ repo: repo(), id: id()! }),
    enabled: !!id(),
    staleTime: Number.POSITIVE_INFINITY,
  }))
}
